import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { 
  SwapHoriz as ChangeIcon, 
  Close as CloseIcon,
  Warning as WarningIcon 
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS, CHANGE_STATUS_OPTIONS, REPACK_STATUS_COLORS } from '../../config';

// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};

function ChangeStatusModal({ open, onClose, onSuccess, entryData, user, restrictedStatuses, sourceScreen }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPartialMove, setIsPartialMove] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '',
    new_status: '',
    move_cartons: 0,
    move_loose_bags: 0,
    move_loose_kgs: 0,
    reason: '',
    fcl_month: '',
    fcl_no: '',
    po_number: '',
  });

  // Load entry data when modal opens
  useEffect(() => {
    if (entryData && open) {
      const nextStatus = getNextStatus(entryData.status);
      setFormData({
        id: entryData.id,
        new_status: nextStatus,
        move_cartons: entryData.cartons || 0,
        move_loose_bags: entryData.loose_bags || 0,
        move_loose_kgs: parseFloat(entryData.loose_kgs) || 0,
        reason: '',
        fcl_month: '',
        fcl_no: '',
        po_number: '',
      });
      setIsPartialMove(false);
    }
  }, [entryData, open]);

  const getNextStatus = (currentStatus) => {
    // If restricted statuses provided (from Repack-Out Dashboard going back), return first one
    if (restrictedStatuses && restrictedStatuses.length > 0) {
      return restrictedStatuses[0];
    }
    
    // Default for Daily Inventory - first option from CHANGE_STATUS_OPTIONS
    return CHANGE_STATUS_OPTIONS[0] || 'REPACK-OUT';
  };

  const getAvailableStatuses = () => {
    // If restrictedStatuses prop is provided (from Repack-Out Dashboard), use only those
    if (restrictedStatuses && restrictedStatuses.length > 0) {
      return restrictedStatuses;
    }
    
    // For Daily Inventory - only show REPACK-OUT, RE-PRINTING, RE-PROCESS
    return CHANGE_STATUS_OPTIONS;
  };

  // Get color styling for status option
  const getStatusColor = (status) => {
    const colorConfig = REPACK_STATUS_COLORS[status];
    if (colorConfig) {
      return {
        backgroundColor: colorConfig.background,
        color: colorConfig.color,
        padding: '4px 12px',
        borderRadius: '4px',
        display: 'inline-block'
      };
    }
    return {};
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    setError('');
  };

  const handlePartialMoveToggle = (checked) => {
    setIsPartialMove(checked);
    if (!checked) {
      // Reset to full quantities
      setFormData({
        ...formData,
        move_cartons: entryData.cartons || 0,
        move_loose_bags: entryData.loose_bags || 0,
        move_loose_kgs: parseFloat(entryData.loose_kgs) || 0,
      });
    }
  };

  const validateQuantities = () => {
    const maxCartons = entryData.cartons || 0;
    const maxBags = entryData.loose_bags || 0;
    const maxKgs = parseFloat(entryData.loose_kgs) || 0;

    if (formData.move_cartons > maxCartons) {
      setError(`Cannot move more than ${maxCartons} cartons`);
      return false;
    }
    if (formData.move_loose_bags > maxBags) {
      setError(`Cannot move more than ${maxBags} loose bags`);
      return false;
    }
    if (formData.move_loose_kgs > maxKgs) {
      setError(`Cannot move more than ${maxKgs.toFixed(2)} loose KGs`);
      return false;
    }

    if (formData.move_cartons === 0 && formData.move_loose_bags === 0 && formData.move_loose_kgs === 0) {
      setError('Must move at least some quantity');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.new_status) {
      setError('Please select a new status');
      return;
    }

    if (!formData.reason) {
      setError('Please provide a reason for status change');
      return;
    }

    if (formData.new_status === 'SHIPPED') {
      if (!formData.fcl_month || !formData.fcl_no) {
        setError('FCL Month and FCL Number are required for shipment');
        return;
      }
    }

    if (!validateQuantities()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        API_ENDPOINTS.CHANGE_STATUS,
        {
          ...formData,
          is_partial: isPartialMove,
        },
        getAuthConfig()
      );

      if (response.data.success) {
        alert('✅ ' + response.data.message);
        onSuccess();
        handleClose();
      }
    } catch (err) {
      console.error('Failed to change status:', err);
      setError(err.response?.data?.error || 'Failed to change status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      id: '',
      new_status: '',
      move_cartons: 0,
      move_loose_bags: 0,
      move_loose_kgs: 0,
      reason: '',
      fcl_month: '',
      fcl_no: '',
      po_number: '',
    });
    setIsPartialMove(false);
    setError('');
    onClose();
  };

  if (!entryData) return null;

  const requiresShipmentInfo = formData.new_status === 'SHIPPED';
  const availableStatuses = getAvailableStatuses();

  // Get dialog title color based on source
  const getDialogColor = () => {
    if (restrictedStatuses && restrictedStatuses.length > 0) {
      return 'secondary.main'; // Purple for Repack-Out dashboard
    }
    return '#ed6c02'; // Orange for Daily Inventory
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ bgcolor: getDialogColor(), color: 'white', fontWeight: 600 }}>
        <ChangeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Change Inventory Status
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Current Item Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Item:</strong> {entryData.product} - {entryData.grade}<br/>
              <strong>Current Status:</strong> {entryData.status}<br/>
              <strong>Available:</strong> {entryData.cartons} cartons, {entryData.loose_bags} bags, {parseFloat(entryData.loose_kgs).toFixed(2)} KGs
            </Typography>
          </Alert>

          {/* Status Change */}
          <Typography variant="h6" gutterBottom>
            New Status
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                required
                label="Change Status To *"
                value={formData.new_status}
                onChange={(e) => handleChange('new_status', e.target.value)}
              >
                {availableStatuses.map((status) => {
                  const colorConfig = REPACK_STATUS_COLORS[status];
                  return (
                    <MenuItem 
                      key={status} 
                      value={status}
                      sx={{
                        '&:hover': {
                          backgroundColor: colorConfig ? `${colorConfig.background}20` : undefined
                        }
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: colorConfig?.background || '#1565c0',
                          color: colorConfig?.color || '#fff',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontWeight: 600
                        }}
                      >
                        {status}
                      </Box>
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          </Grid>

          {/* Partial Move Option */}
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPartialMove}
                  onChange={(e) => handlePartialMoveToggle(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Partial Quantity Move
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Move only part of the quantity (splits the entry)
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Quantities to Move */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Quantities to Move
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Cartons"
                value={formData.move_cartons}
                onChange={(e) => handleChange('move_cartons', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: entryData.cartons }}
                disabled={!isPartialMove}
                helperText={!isPartialMove ? 'Full quantity' : `Max: ${entryData.cartons}`}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Loose Bags"
                value={formData.move_loose_bags}
                onChange={(e) => handleChange('move_loose_bags', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: entryData.loose_bags }}
                disabled={!isPartialMove}
                helperText={!isPartialMove ? 'Full quantity' : `Max: ${entryData.loose_bags}`}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Loose KGs"
                value={formData.move_loose_kgs}
                onChange={(e) => handleChange('move_loose_kgs', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, max: parseFloat(entryData.loose_kgs), step: 0.01 }}
                disabled={!isPartialMove}
                helperText={!isPartialMove ? 'Full quantity' : `Max: ${parseFloat(entryData.loose_kgs).toFixed(2)}`}
              />
            </Grid>
          </Grid>

          {/* Shipment Info (if status = SHIPPED) */}
          {requiresShipmentInfo && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Shipment Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="FCL Month *"
                    value={formData.fcl_month}
                    onChange={(e) => handleChange('fcl_month', e.target.value)}
                    placeholder="e.g., JAN-2025"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="FCL Number *"
                    value={formData.fcl_no}
                    onChange={(e) => handleChange('fcl_no', e.target.value)}
                    placeholder="e.g., FCL-001"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="PO Number"
                    value={formData.po_number}
                    onChange={(e) => handleChange('po_number', e.target.value)}
                    placeholder="e.g., PO-2025-001"
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Reason */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Reason for Change
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TextField
            fullWidth
            multiline
            rows={3}
            required
            label="Reason *"
            value={formData.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            placeholder="Explain why you're changing the status..."
            helperText="This will be logged in the change log"
          />

          {/* Warning for Partial Move */}
          {isPartialMove && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon />
                <Typography variant="body2">
                  <strong>Partial Move:</strong> This will create a new entry with the moved quantity and reduce the original entry accordingly.
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Info about destination */}
          {!restrictedStatuses && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Items with status <strong>REPACK-OUT</strong>, <strong>RE-PRINTING</strong>, or <strong>RE-PROCESS</strong> will appear in the <strong>Repack-Out Dashboard</strong>.
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.100' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            startIcon={<CloseIcon />}
            size="large"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <ChangeIcon />}
            size="large"
            sx={{
              bgcolor: REPACK_STATUS_COLORS[formData.new_status]?.background || '#ed6c02',
              '&:hover': {
                bgcolor: REPACK_STATUS_COLORS[formData.new_status]?.background || '#d55b02',
                filter: 'brightness(0.9)'
              }
            }}
          >
            {loading ? 'Processing...' : 'Change Status'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ChangeStatusModal;